import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, ShieldCheckIcon, LockIcon } from 'lucide-react';
export type PlanId = 'completo' | 'kitpro';
const CHECKOUT_URLS: Record<PlanId, string> = {
  completo: 'https://checkout.complementodigital.shop/cart/40771159851137:1',
  kitpro: 'https://checkout.complementodigital.shop/cart/40772030529665:1'
};
interface PricingCardsProps {
  selectedPlan: PlanId;
  onSelectPlan: (plan: PlanId) => void;
}
export function PricingCards({
  selectedPlan,
  onSelectPlan
}: PricingCardsProps) {
  const handleCheckout = () => {
    window.open(CHECKOUT_URLS[selectedPlan], '_blank', 'noopener,noreferrer');
  };
  const ctaPrice = selectedPlan === 'kitpro' ? '$24.990' : '$19.990';
  const ctaLabel = selectedPlan === 'kitpro' ? 'QUIERO EL KIT PRO' : 'QUIERO EL PLAN COMPLETO';
  return <div className="max-w-4xl mx-auto" data-id="element-195">
      <div className="grid sm:grid-cols-2 gap-4" data-id="element-196">
        {/* Plan Completo */}
        <PlanOption id="completo" selected={selectedPlan === 'completo'} onSelect={() => onSelectPlan('completo')} title="PLAN COMPLETO" price="$19.990" oldPrice="$59.900" features={[{
        text: '6 Fases',
        included: true
      }, {
        text: '6 Bonus',
        included: true
      }, {
        text: 'Sin Bonus Plus Premium',
        included: false
      }]} accent="primary" data-id="element-197" />

        {/* KIT PRO */}
        <PlanOption id="kitpro" selected={selectedPlan === 'kitpro'} onSelect={() => onSelectPlan('kitpro')} title="KIT PRO" price="$24.990" oldPrice="$59.900" features={[{
        text: '6 Fases',
        included: true
      }, {
        text: '6 Bonus',
        included: true
      }, {
        text: '3 Bonus Plus Premium',
        included: true,
        highlight: true
      }, {
        text: 'Acceso completo a todos los recursos',
        included: true
      }]} accent="gold" recommended data-id="element-198" />
      </div>

      {/* Upsell hint */}
      <motion.div initial={false} animate={{
      opacity: selectedPlan === 'completo' ? 1 : 0,
      height: selectedPlan === 'completo' ? 'auto' : 0,
      marginTop: selectedPlan === 'completo' ? 16 : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-199">
        <div className="p-3 bg-gold/10 rounded-xl border border-gold/20 text-center" data-id="element-200">
          <p className="text-sm text-gold-light" data-id="element-201">
            ⚡ Por solo <strong data-id="element-202">$5.000 más</strong> obtenés los 3 Bonus Plus
            Premium del KIT PRO
          </p>
        </div>
      </motion.div>

      {/* Shared CTA */}
      <motion.div layout className="mt-6" data-id="element-203">
        <button onClick={handleCheckout} className={`group w-full py-4 rounded-2xl font-bold text-base sm:text-lg transition-all flex items-center justify-center gap-3 hover:scale-[1.01] active:scale-[0.99] ${selectedPlan === 'kitpro' ? 'bg-gold hover:bg-gold-hover text-black shadow-[0_0_30px_-5px_rgba(212,165,116,0.5)]' : 'bg-primary hover:bg-primary-hover text-text-dark shadow-[0_0_30px_-5px_rgba(16,185,129,0.4)]'}`} data-id="element-204">
          {ctaLabel} — {ctaPrice} ARS
          <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-205" />
        </button>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-text-muted" data-id="element-206">
          <div className="flex items-center gap-1.5" data-id="element-207">
            <LockIcon className="w-3.5 h-3.5" data-id="element-208" />
            <span data-id="element-209">Pago 100% seguro</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-210">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-211" />
            <span data-id="element-212">Acceso inmediato</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-213">
            <CheckIcon className="w-3.5 h-3.5" data-id="element-214" />
            <span data-id="element-215">Garantía de satisfacción</span>
          </div>
        </div>
      </motion.div>
    </div>;
}
interface PlanOptionProps {
  id: PlanId;
  selected: boolean;
  onSelect: () => void;
  title: string;
  price: string;
  oldPrice: string;
  features: {
    text: string;
    included: boolean;
    highlight?: boolean;
  }[];
  accent: 'primary' | 'gold';
  recommended?: boolean;
}
function PlanOption({
  selected,
  onSelect,
  title,
  price,
  oldPrice,
  features,
  accent,
  recommended
}: PlanOptionProps) {
  const isGold = accent === 'gold';
  return <motion.button type="button" onClick={onSelect} whileHover={{
    y: -2
  }} className={`relative text-left rounded-2xl p-5 sm:p-6 border-2 transition-all flex flex-col ${selected ? isGold ? 'border-gold bg-gold/[0.06] shadow-[0_0_30px_-10px_rgba(212,165,116,0.4)]' : 'border-primary bg-primary/[0.06] shadow-[0_0_30px_-10px_rgba(16,185,129,0.3)]' : 'border-surface-light bg-surface hover:border-text-muted/40'}`} data-id="element-216">
      {recommended && <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 uppercase tracking-wider whitespace-nowrap ${isGold ? 'bg-gold text-black' : 'bg-primary text-text-dark'}`} data-id="element-217">
          <span data-id="element-218">🔥</span> MÁS ELEGIDO
        </div>}

      <div className="flex items-start justify-between gap-3 mb-3" data-id="element-219">
        <div className="flex items-center gap-3" data-id="element-220">
          {/* Radio indicator */}
          <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? isGold ? 'border-gold' : 'border-primary' : 'border-text-muted/50'}`} data-id="element-221">
            {selected && <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} className={`w-2.5 h-2.5 rounded-full ${isGold ? 'bg-gold' : 'bg-primary'}`} data-id="element-222" />}
          </div>
          <h3 className={`font-serif text-xl ${isGold && selected ? 'text-gold' : 'text-white'}`} data-id="element-223">
            {title}
          </h3>
        </div>
      </div>

      <div className="flex items-baseline gap-2 mb-4" data-id="element-224">
        <span className="text-3xl font-bold tabular-nums text-white" data-id="element-225">
          {price}
        </span>
        <span className="text-text-muted line-through text-sm" data-id="element-226">{oldPrice}</span>
      </div>

      <ul className="space-y-2" data-id="element-227">
        {features.map((f, i) => <li key={i} className={`flex items-center gap-2 text-sm ${f.included ? '' : 'opacity-40'}`} data-id="element-228">
            {f.included ? <CheckIcon className={`w-4 h-4 flex-shrink-0 ${isGold ? 'text-gold' : 'text-primary'}`} data-id="element-229" /> : <span className="w-4 h-4 flex items-center justify-center text-text-muted" data-id="element-230">
                –
              </span>}
            <span className={f.highlight ? `font-semibold ${isGold ? 'text-gold-light' : 'text-primary-light'}` : ''} data-id="element-231">
              {f.text}
            </span>
          </li>)}
      </ul>
    </motion.button>;
}